import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { CONTAINER_CLASS } from "../constants/layout";
import {
  Phone, MapPin, Clock, Droplets, Flame, Wind, Waves, Bath,
  CheckCircle, ArrowRight, Star, Building2, Send, Users, Truck,
  Shield, ChevronRight, ChevronLeft, HardHat, ShoppingCart, Package,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { EPP, googleReviewsUrl, catalogueFamilies } from "../data/content";
import { demoHeadersExtra } from "../constants/demoMode";
import { useStore } from "../context/StoreContext";
import { useSiteContent } from "../hooks/useSiteContent";
import { useProducts } from "../hooks/useCatalog";
import ProductCard, { ProductCardSkeleton } from "../components/catalog/ProductCard";
import { useResolvedPriceBulk } from "../hooks/useStoresPricingStock";
import { useUserAuth } from "../context/UserAuthContext";
import { HomeEventsPreview } from "../components/marketing/HomeEventsPreview";
import { AgenciesSection } from "../components/AgenciesSection";
import { EppTricolorRingButton } from "../components/EppTricolorRingButton";
import { resolveMarketingHref, resolveSecondaryMarketingHref, contentEventDateLine } from "../utils/marketingHref";
import { recordCampaignClick, recordContentEventClick } from "../utils/campaignTracking";
import {
  heroTagForCampaign,
  sliderTagForPackRow,
  storefrontUrgencyPrimary,
  urgencyEndToIso,
} from "../constants/campaignMarketing";
import { SpotlightBanner } from "../components/marketing/SpotlightBanner";
import { UrgencyCountdown } from "../components/marketing/UrgencyCountdown";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const ease = [0.22, 1, 0.36, 1];
const fadeUp = { initial: { opacity: 0, y: 25 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-50px" }, transition: { duration: 0.6, ease } };

const heroSlides = [
  { image: "https://images.unsplash.com/photo-1758548157466-7c454382035a?w=1200&q=80", tag: "En stock maintenant", title: "Vos matériaux pro — dispo aujourd'hui", description: "3 000+ références en stock. Retrait en 2 h dans nos 5 agences.", cta: "Acheter maintenant", ctaLink: "/catalogue", cta2: "Voir les produits en stock", cta2Link: "/catalogue?in_stock=true", accent: "#190276" },
  { image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&q=80", tag: "Promotions en cours", title: "Jusqu'à -30 % sur une sélection pro", description: "Remises exclusives sur des centaines de produits. Profitez-en avant épuisement du stock !", cta: "Voir les promotions", ctaLink: "/promos", cta2: "Découvrir le catalogue", cta2Link: "/catalogue", accent: "#f7040a" },
  { image: "https://europlomberie-06.fr/uploads/media/images/cms/medias/thumb_/cms/medias/689b07a13e781_images_medium.webp", tag: "Piscines", title: "Équipement piscine — en stock, prix pro", description: "Filtration, robots, pompes à chaleur. Retrait immédiat en agence ou livraison.", cta: "Voir les produits disponibles", ctaLink: "/catalogue", accent: "#F2B531" },
  { image: "https://europlomberie-06.fr/uploads/media/images/cms/medias/thumb_/cms/medias/689b29020cbec_images_medium.webp", tag: "Plomberie & chauffage", title: "Tubes, raccords, chaudières — retrait immédiat", description: "Stock massif dans nos 5 agences de retrait. Prix pro garantis.", cta: "Commander maintenant", ctaLink: "/catalogue", accent: "#f7040a" },
  { image: "https://images.unsplash.com/photo-1761330440311-16e160cad236?w=1200&q=80", tag: "Climatisation", title: "Splits & gainables — meilleures marques, stock réel", description: "Prix compétitifs, devis gratuit en 24h. Commande en ligne simplifiée.", cta: "Voir les prix", ctaLink: "/catalogue", accent: "#190276" },
  { image: "https://europlomberie-06.fr/uploads/media/images/cms/medias/thumb_/cms/medias/689b0d1d946d0_images_medium.webp", tag: "Salle de bains", title: "Robinetterie, douches, meubles — showroom 600 m²", description: "40 ambiances au Cannet. Achetez en ligne ou venez comparer en showroom.", cta: "Acheter en ligne", ctaLink: "/catalogue", accent: "#F2B531" },
];

/** Encarts pub / mise en avant — colonne droite du hero (desktop). Modifiez href, textes, images. */
const heroPromoCards = [
  {
    id: "hero-promo-1",
    kicker: "Promotion",
    title: "Offres du moment",
    subtitle: "Remises sur une sélection de produits — quantités limitées.",
    href: "/promos",
    image: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=800&q=80",
    accent: "#190276",
  },
  {
    id: "hero-promo-2",
    kicker: "Showroom",
    title: "600 m² au Cannet",
    subtitle: "Comparez les finitions sur place avec nos conseillers.",
    href: "/showroom",
    image: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800&q=80",
    accent: "#F2B531",
  },
];

const services = [
  { id: "plomberie", title: "Plomberie", subtitle: "Tubes, raccords & accessoires", icon: Droplets, color: "#190276", image: "https://europlomberie-06.fr/uploads/media/images/cms/medias/thumb_/cms/medias/689b29020cbec_images_medium.webp", description: "Un large choix de matériel de plomberie de qualité pour tous vos travaux. Disponible en ligne et en agence.", features: ["Tubes et raccords", "Robinetterie", "Cuves et gouttières", "Outillage pro"], link: "/catalogue", pageLink: "/plomberie" },
  { id: "chauffage", title: "Chauffage", subtitle: "Pompes à chaleur & chaudières", icon: Flame, color: "#f7040a", image: "https://europlomberie-06.fr/uploads/media/images/cms/medias/thumb_/cms/medias/689b2e2dd5585_images_medium.webp", description: "Solutions de chauffage performantes. Commandez en ligne, retirez en agence.", features: ["Pompe a chaleur", "Chaudiere", "Plancher chauffant", "Radiateurs"], link: "/catalogue", pageLink: "/chauffage" },
  { id: "climatisation", title: "Climatisation", subtitle: "Split & gainable", icon: Wind, color: "#190276", image: "https://europlomberie-06.fr/uploads/media/images/cms/medias/thumb_/cms/medias/689b3450c982b_images_medium.webp", description: "Climatiseurs réversibles au meilleur prix. Devis et commande en ligne.", features: ["Monosplit", "Multisplit", "Gainable", "VRF / VRV"], link: "/catalogue", pageLink: "/climatisation" },
  { id: "piscines", title: "Piscines", subtitle: "Équipement complet", icon: Waves, color: "#190276", image: "https://europlomberie-06.fr/uploads/media/images/cms/medias/thumb_/cms/medias/689b07a13e781_images_medium.webp", description: "Spécialiste piscine depuis 25 ans. Tout disponible en ligne.", features: ["Filtration", "Robots", "Traitement eau", "Pompes"], link: "/catalogue", pageLink: "/piscines" },
  { id: "sdb", title: "Salle de bains", subtitle: "Design & tendances", icon: Bath, color: "#F2B531", image: "https://europlomberie-06.fr/uploads/media/images/cms/medias/thumb_/cms/medias/689b0d1d946d0_images_medium.webp", description: "Robinetterie, douches, meubles. Achetez en ligne ou en showroom.", features: ["Robinetterie", "Baignoires", "Parois de douche", "Meubles"], link: "/catalogue", pageLink: "/showroom" },
];

const HeroSection = ({ apiHeroRows = [] }) => {
  const [injectedSlides, setInjectedSlides] = useState([]);
  useEffect(() => {
    if (!apiHeroRows.length) {
      setInjectedSlides([]);
      return;
    }
    const mapped = apiHeroRows.map((row) => {
      const href = resolveMarketingHref(row);
      const secHref = resolveSecondaryMarketingHref(row);
      const descParts = [row.marketing_hook, row.subtitle].filter((x) => x && String(x).trim());
      const cta2Label = row.cta_secondary_label?.trim() || null;
      const track = row.__source === "event" ? "event" : "campaign";
      const uEnd = urgencyEndToIso(row.urgency_end_date);
      return {
        image: row.image || heroSlides[0].image,
        tag: heroTagForCampaign(row),
        urgencyBadge: storefrontUrgencyPrimary(row),
        urgencyEndIso: uEnd,
        title: row.title,
        description: descParts.length ? descParts.join(" · ") : "",
        cta: row.cta_label || "Découvrir",
        ctaLink: href,
        cta2: cta2Label && secHref ? cta2Label : null,
        cta2Link: cta2Label && secHref ? secHref : null,
        accent: row.accent_color || "#190276",
        _externalPrimary: href.startsWith("http"),
        _externalSecondary: secHref.startsWith("http"),
        _trackId: row.id,
        _trackType: track,
      };
    });
    setInjectedSlides(mapped);
  }, [apiHeroRows]);

  const mergedSlides = useMemo(() => {
    if (!injectedSlides.length) return heroSlides;
    return [...heroSlides.slice(0, 1), ...injectedSlides, ...heroSlides.slice(1)];
  }, [injectedSlides]);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const nSlides = mergedSlides.length;
  const goToSlide = useCallback((index) => { setCurrentSlide(index); setProgress(0); setIsAutoPlaying(false); clearTimeout(timerRef.current); timerRef.current = setTimeout(() => setIsAutoPlaying(true), 8000); }, []);
  const nextSlide = useCallback(() => { setCurrentSlide((prev) => (prev + 1) % nSlides); setProgress(0); }, [nSlides]);
  const prevSlide = useCallback(() => { setCurrentSlide((prev) => (prev - 1 + nSlides) % nSlides); setProgress(0); }, [nSlides]);

  useEffect(() => {
    setCurrentSlide((i) => Math.min(i, Math.max(0, nSlides - 1)));
  }, [nSlides]);

  useEffect(() => {
    if (!isAutoPlaying || isPaused) return;
    const interval = setInterval(nextSlide, 6000);
    const progressInterval = setInterval(() => setProgress((p) => Math.min(p + 100 / 60, 100)), 100);
    return () => { clearInterval(interval); clearInterval(progressInterval); };
  }, [isAutoPlaying, isPaused, nextSlide]);

  const slide = mergedSlides[currentSlide];

  return (
    <section className="bg-white" data-testid="hero-section">
      <div className={`${CONTAINER_CLASS} pt-2 sm:pt-3 md:pt-4 pb-2 sm:pb-3`}>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-3 lg:items-stretch lg:min-h-[420px] lg:h-[min(500px,56vh)] xl:gap-4">
          <div
            className="min-h-0 rounded-xl sm:rounded-2xl p-[2px] bg-gradient-to-br from-[#190276] via-[#F2B531] to-[#f7040a] shadow-lg shadow-[#190276]/20 lg:col-span-2 lg:h-full"
            data-testid="hero-slider"
          >
            <div
              className="relative min-h-0 overflow-hidden rounded-[11px] sm:rounded-[14px] bg-gray-900 h-full group"
            >
            <div
              className="relative h-[340px] sm:h-[400px] md:h-[460px] lg:h-full lg:min-h-0"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <AnimatePresence mode="wait">
                <motion.div key={currentSlide} initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.7 }} className="absolute inset-0">
                  <img src={slide.image} alt={slide.title} className="h-full w-full object-cover" />
                  {/* Démo lisibilité : double calque (latéral + bas) pour fonds très clairs */}
                  <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/[0.82] via-black/[0.5] to-black/25 lg:to-black/[0.38]"
                    aria-hidden
                  />
                  <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent sm:from-black/45"
                    aria-hidden
                  />
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full max-w-3xl px-5 sm:pl-[5.25rem] sm:pr-[5.25rem] md:pl-28 md:pr-28 lg:pl-20 lg:pr-16 xl:pl-24 xl:pr-24">
                      <div className="mb-3 flex flex-wrap items-center gap-2 sm:mb-4">
                        <motion.span
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.15 }}
                          className="inline-block rounded-md px-3 py-1 text-[10px] font-black uppercase tracking-[0.15em] text-white shadow-[0_2px_12px_rgba(0,0,0,0.45)] ring-1 ring-white/25 sm:text-[11px]"
                          style={{ backgroundColor: slide.accent }}
                        >
                          {slide.tag}
                        </motion.span>
                        {slide.urgencyBadge ? (
                          <motion.span
                            initial={{ opacity: 0, x: -12 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                            className="inline-block rounded-md bg-[#f7040a] px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white shadow-lg ring-1 ring-white/30 sm:text-[11px]"
                          >
                            {slide.urgencyBadge}
                          </motion.span>
                        ) : null}
                      </div>
                      <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                        className="font-heading mb-2.5 text-[24px] font-black leading-[1.1] tracking-tight text-white [text-shadow:0_2px_16px_rgba(0,0,0,0.85),0_1px_3px_rgba(0,0,0,0.9)] sm:mb-3 sm:text-[2.5rem] md:text-[2.75rem] lg:text-[2.85rem]"
                        data-testid="hero-title"
                      >
                        {slide.title}
                      </motion.h2>
                      {slide.urgencyEndIso ? (
                        <UrgencyCountdown
                          endIso={slide.urgencyEndIso}
                          className="mb-2 text-[11px] font-black uppercase tracking-wider text-[#F2B531] drop-shadow-md sm:mb-3 sm:text-xs"
                        />
                      ) : null}
                      <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="mb-4 max-w-lg text-[13px] leading-relaxed text-white/95 sm:mb-5 sm:text-[15px] [text-shadow:0_1px_8px_rgba(0,0,0,0.75)]"
                      >
                        {slide.description}
                      </motion.p>
                      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mb-3 flex flex-col gap-2 sm:mb-5 sm:flex-row sm:flex-wrap sm:gap-3">
                        <div className="inline-flex w-full shrink-0 flex-col overflow-hidden rounded-xl shadow-lg ring-1 ring-white/25 sm:w-auto">
                          {slide._externalPrimary ? (
                            <a
                              href={slide.ctaLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex w-full items-center justify-center gap-2 px-5 py-3 text-center text-[12px] font-black uppercase tracking-wide text-white transition-all hover:brightness-110 sm:gap-2.5 sm:px-6 sm:py-3.5 sm:text-[13px] sm:text-sm"
                              style={{ backgroundColor: slide.accent }}
                              data-testid="hero-cta-primary"
                              onClick={() => {
                                if (slide._trackType === "event") recordContentEventClick(slide._trackId);
                                else if (slide._trackType) recordCampaignClick(slide._trackId);
                              }}
                            >
                              {slide.cta} <ArrowRight className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                            </a>
                          ) : (
                            <Link
                              to={slide.ctaLink || "/catalogue"}
                              className="inline-flex w-full items-center justify-center gap-2 px-5 py-3 text-center text-[12px] font-black uppercase tracking-wide text-white transition-all hover:brightness-110 sm:gap-2.5 sm:px-6 sm:py-3.5 sm:text-[13px] sm:text-sm"
                              style={{ backgroundColor: slide.accent }}
                              data-testid="hero-cta-primary"
                              onClick={() => {
                                if (slide._trackType === "event") recordContentEventClick(slide._trackId);
                                else if (slide._trackType) recordCampaignClick(slide._trackId);
                              }}
                            >
                              {slide.cta} <ArrowRight className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                            </Link>
                          )}
                          <div className="flex h-[3px] w-full shrink-0" aria-hidden>
                            <div className="flex-1 bg-[#190276]" />
                            <div className="flex-1 bg-[#F2B531]" />
                            <div className="flex-1 bg-[#f7040a]" />
                          </div>
                        </div>
                        {slide.cta2 && slide.cta2Link && (slide._externalSecondary ? (
                          <a
                            href={slide.cta2Link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/10 px-4 py-2.5 text-center text-[12px] font-bold leading-snug text-white backdrop-blur-md transition-all hover:bg-white/20 sm:w-auto sm:px-5 sm:py-3.5 sm:text-sm"
                            onClick={() => {
                              if (slide._trackType === "event") recordContentEventClick(slide._trackId);
                              else if (slide._trackType) recordCampaignClick(slide._trackId);
                            }}
                          >
                            <MapPin className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
                            {slide.cta2}
                          </a>
                        ) : (
                          <Link
                            to={slide.cta2Link}
                            className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-xl border border-white/25 bg-white/10 px-4 py-2.5 text-center text-[12px] font-bold leading-snug text-white backdrop-blur-md transition-all hover:bg-white/20 sm:w-auto sm:px-5 sm:py-3.5 sm:text-sm"
                            onClick={() => {
                              if (slide._trackType === "event") recordContentEventClick(slide._trackId);
                              else if (slide._trackType) recordCampaignClick(slide._trackId);
                            }}
                          >
                            <MapPin className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" aria-hidden />
                            {slide.cta2}
                          </Link>
                        ))}
                      </motion.div>
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="flex flex-wrap gap-2 sm:gap-3">
                        {[{ icon: Building2, text: "5 agences + showroom" }, { icon: Package, text: "3 000+ produits" }, { icon: Truck, text: "Retrait en 2h" }].map((b, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/10 px-2 py-1 text-[10px] font-bold text-white/90 backdrop-blur-sm sm:text-[10.5px]"><b.icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> {b.text}</span>
                        ))}
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-[3.25rem] sm:block">
                <div className="flex h-full items-center justify-center pl-1">
                  <div className="pointer-events-auto">
                    <EppTricolorRingButton
                      buttonClassName="h-10 w-10 sm:h-11 sm:w-11"
                      onClick={prevSlide}
                      aria-label="Précédent"
                    >
                      <ChevronLeft className="h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]" strokeWidth={2.25} />
                    </EppTricolorRingButton>
                  </div>
                </div>
              </div>
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-[3.25rem] sm:block">
                <div className="flex h-full items-center justify-center pr-1">
                  <div className="pointer-events-auto">
                    <EppTricolorRingButton
                      buttonClassName="h-10 w-10 sm:h-11 sm:w-11"
                      onClick={nextSlide}
                      aria-label="Suivant"
                    >
                      <ChevronRight className="h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]" strokeWidth={2.25} />
                    </EppTricolorRingButton>
                  </div>
                </div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 z-10">
                <div className="flex">
                  {mergedSlides.map((_, i) => (
                    <button key={i} type="button" onClick={() => goToSlide(i)} className="relative h-1 flex-1" aria-label={`Slide ${i + 1}`}>
                      <div className="absolute inset-0 bg-white/20" />
                      {i === currentSlide && <motion.div className="absolute inset-y-0 left-0 bg-white" style={{ width: `${progress}%` }} />}
                      {i < currentSlide && <div className="absolute inset-0 bg-white/60" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            </div>
          </div>

          <aside className="flex min-h-0 flex-col gap-3 lg:col-span-1 lg:h-full lg:min-h-0" aria-label="Encarts promotionnels">
            {heroPromoCards.map((card) => (
              <div
                key={card.id}
                className="flex min-h-[140px] flex-1 basis-0 flex-col rounded-xl p-[2px] bg-gradient-to-br from-[#190276] via-[#F2B531] to-[#f7040a] shadow-md shadow-[#190276]/15 transition hover:shadow-lg sm:min-h-[150px] lg:min-h-0"
              >
              <Link
                to={card.href}
                className="group relative flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] bg-gray-100 shadow-sm transition sm:min-h-[146px] lg:min-h-0"
                data-testid={`hero-promo-${card.id}`}
              >
                {card.image ? (
                  <img src={card.image} alt={card.title} className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                ) : null}
                <div
                  className={`absolute inset-0 ${card.image ? "bg-gradient-to-t from-black/85 via-black/45 to-black/25" : ""}`}
                  style={card.image ? undefined : { background: `linear-gradient(145deg, ${card.accent}, ${card.accent}cc)` }}
                  aria-hidden
                />
                <div className="relative flex h-full flex-col justify-end p-4 text-white sm:p-5">
                  <span className="inline-block max-w-fit rounded px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.2em]" style={{ backgroundColor: `${card.accent}e6` }}>{card.kicker}</span>
                  <h3 className="mt-2 font-heading text-base font-black leading-tight sm:text-lg">{card.title}</h3>
                  <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-white/90 sm:text-xs">{card.subtitle}</p>
                  <span className="mt-2.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#F2B531] sm:text-[11px]">
                    Découvrir <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
              </div>
            ))}
          </aside>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════
   PRODUITS — SCROLL HORIZONTAL UNIQUE
   ═══════════════════════════════════════════════ */
const ProductsHorizontalScroll = ({ items, loading, title, badge, badgeColor, link, linkLabel, dark = false, bulkResolved = {} }) => {
  const ref = useRef(null);
  const scroll = (dir) => { const c = ref.current; if (!c) return; const w = c.firstElementChild ? c.firstElementChild.offsetWidth + 16 : 280; c.scrollBy({ left: dir * w, behavior: "smooth" }); };

  if (loading && items.length === 0) return (
    <div className="py-12"><div className={CONTAINER_CLASS}>
      <div className="mb-6"><div className={`h-6 w-48 rounded animate-pulse ${dark ? "bg-white/20" : "bg-gray-200"}`} /></div>
      <div className="flex gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="shrink-0 w-[220px]"><ProductCardSkeleton /></div>)}</div>
    </div></div>
  );
  if (items.length === 0) return null;

  return (
    <div className="py-10 md:py-14">
      <div className={CONTAINER_CLASS}>
        <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            {badge && <span className="inline-block px-2.5 py-1 rounded-lg text-white text-[10px] font-black uppercase tracking-wide" style={{ backgroundColor: badgeColor }}>{badge}</span>}
            <h2 className={`font-heading text-xl md:text-2xl font-black tracking-tight ${dark ? "text-white" : "text-gray-900"}`}>{title}</h2>
          </div>
          <Link to={link} className={`inline-flex items-center gap-1.5 text-[13px] font-bold hover:gap-2.5 transition-all ${dark ? "text-[#F2B531] hover:text-[#f7d36a]" : "text-[#190276]"}`}>{linkLabel} <ChevronRight className="w-3.5 h-3.5" /></Link>
        </motion.div>

        <div className="relative group/scroll">
          <div ref={ref} className="flex gap-4 overflow-x-auto pb-2 snap-x snap-proximity" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {items.map((p) => (
              <div key={p.id} className="shrink-0 w-[200px] sm:w-[220px] md:w-[240px] snap-start">
                <ProductCard product={p} showStock preResolved={bulkResolved[p.id] || null} />
              </div>
            ))}
          </div>
          <button type="button" onClick={() => scroll(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 shadow-xl border border-gray-200/60 flex items-center justify-center text-gray-700 hover:text-[#190276] hover:scale-105 opacity-0 group-hover/scroll:opacity-100 transition-all duration-300 z-10 backdrop-blur-sm" aria-label="Précédent"><ChevronLeft className="w-5 h-5" /></button>
          <button type="button" onClick={() => scroll(1)} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 shadow-xl border border-gray-200/60 flex items-center justify-center text-gray-700 hover:text-[#190276] hover:scale-105 opacity-0 group-hover/scroll:opacity-100 transition-all duration-300 z-10 backdrop-blur-sm" aria-label="Suivant"><ChevronRight className="w-5 h-5" /></button>
        </div>
      </div>
    </div>
  );
};

const FeaturedProducts = () => {
  const { selectedId: featStoreId } = useStore();
  const { items, loading } = useProducts({ featured: true, limit: 12, store_id: featStoreId || undefined });
  const { token } = useUserAuth();
  const bulkIds = useMemo(() => items.map((p) => p.id).filter(Boolean), [items]);
  const { results: bulkResolved } = useResolvedPriceBulk(bulkIds, token);
  const sliderRef = useRef(null);
  const scrollFeat = (dir) => { const c = sliderRef.current; if (!c) return; const w = c.firstElementChild ? c.firstElementChild.offsetWidth + 16 : 260; c.scrollBy({ left: dir * w, behavior: "smooth" }); };

  if (loading && items.length === 0) return (
    <div className="py-12"><div className={CONTAINER_CLASS}><div className="flex gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="shrink-0 w-[220px]"><ProductCardSkeleton /></div>)}</div></div></div>
  );
  if (items.length === 0) return null;

  return (
    <div className={CONTAINER_CLASS}>
      <div className="bg-[#190276] rounded-lg sm:rounded-xl overflow-hidden">
        <div className="px-3 py-2.5 sm:px-5 sm:py-3 md:px-8 md:py-3.5">
          {/* Mobile : une seule ligne, pas de gros bouton ni badge */}
          <div className="flex items-center justify-between gap-3 sm:hidden">
            <h2 className="min-w-0 flex-1 font-heading text-[14px] font-black leading-tight tracking-tight text-white">
              Produits les plus commandés
            </h2>
            <Link
              to="/catalogue"
              className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-bold text-[#F2B531] underline-offset-2 hover:text-[#f7d36a] hover:underline"
            >
              Tout voir <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
            </Link>
          </div>
          {/* sm+ : bandeau habituel */}
          <div className="hidden sm:block">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#F2B531]">Sélection pro</span>
              <Link
                to="/catalogue"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#F2B531] px-4 py-2 text-[12px] font-bold text-gray-900 transition-colors hover:bg-[#e5a82a]"
              >
                Tout voir <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <h2 className="mt-1 font-heading text-base font-black tracking-tight text-white md:text-lg">
              Produits les plus commandés
            </h2>
          </div>
        </div>
        <div className="flex h-[2px] sm:h-[3px]">
          <div className="flex-1 bg-[#190276]" />
          <div className="flex-1 bg-[#F2B531]" />
          <div className="flex-1 bg-[#f7040a]" />
        </div>
      </div>
      <div className="bg-gray-100 rounded-lg sm:rounded-xl overflow-hidden mt-1 sm:mt-1.5 relative group/scroll">
        <div className="overflow-hidden px-3 sm:px-4 md:px-8 py-4 sm:py-5">
          <div ref={sliderRef} className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory" style={{ scrollbarWidth: "none" }}>
            {items.map((p) => (
              <div key={p.id} className="shrink-0 w-[165px] sm:w-[210px] md:w-[235px] lg:w-[240px] snap-start bg-white rounded-xl overflow-hidden">
                <ProductCard product={p} showStock preResolved={bulkResolved[p.id] || null} />
              </div>
            ))}
          </div>
        </div>
        <button type="button" onClick={() => scrollFeat(-1)} className="absolute left-1.5 sm:left-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#190276]/80 sm:bg-[#190276] hover:bg-[#130160] text-white flex items-center justify-center transition-all z-10 shadow-lg md:opacity-0 md:group-hover/scroll:opacity-100" aria-label="Précédent"><ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" /></button>
        <button type="button" onClick={() => scrollFeat(1)} className="absolute right-1.5 sm:right-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#190276]/80 sm:bg-[#190276] hover:bg-[#130160] text-white flex items-center justify-center transition-all z-10 shadow-lg md:opacity-0 md:group-hover/scroll:opacity-100" aria-label="Suivant"><ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" /></button>
      </div>
    </div>
  );
};

const NewProducts = () => {
  const { selectedId: newStoreId } = useStore();
  const { items, loading } = useProducts({ sort: "newest", limit: 12, store_id: newStoreId || undefined });
  const { token } = useUserAuth();
  const bulkIds = useMemo(() => items.map((p) => p.id).filter(Boolean), [items]);
  const { results: bulkResolved } = useResolvedPriceBulk(bulkIds, token);
  const newRef = useRef(null);
  const scrollNew = (dir) => { const c = newRef.current; if (!c) return; const w = c.firstElementChild ? c.firstElementChild.offsetWidth + 16 : 260; c.scrollBy({ left: dir * w, behavior: "smooth" }); };

  if (loading && items.length === 0) return (
    <div className="py-12"><div className={CONTAINER_CLASS}><div className="flex gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="shrink-0 w-[220px]"><ProductCardSkeleton /></div>)}</div></div></div>
  );
  if (items.length === 0) return null;

  return (
    <div className={CONTAINER_CLASS}>
      <div className="bg-[#190276] rounded-lg sm:rounded-xl overflow-hidden">
        <div className="px-3 py-2.5 sm:px-5 sm:py-3 md:px-8 md:py-3.5">
          <div className="flex items-center justify-between gap-3 sm:hidden">
            <h2 className="min-w-0 flex-1 font-heading text-[14px] font-black leading-tight tracking-tight text-white">
              Vient d&apos;arriver en stock
            </h2>
            <Link
              to="/catalogue?sort=newest"
              className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-bold text-[#F2B531] underline-offset-2 hover:text-[#f7d36a] hover:underline"
            >
              Tout voir <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
            </Link>
          </div>
          <div className="hidden sm:block">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-block rounded-lg bg-[#f7040a] px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-white">Nouveau</span>
              <Link
                to="/catalogue?sort=newest"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#F2B531] px-4 py-2 text-[12px] font-bold text-gray-900 transition-colors hover:bg-[#e5a82a]"
              >
                Tout voir <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <h2 className="mt-1 font-heading text-base font-black tracking-tight text-white md:text-lg">
              Vient d&apos;arriver en stock
            </h2>
          </div>
        </div>
        <div className="flex h-[2px] sm:h-[3px]">
          <div className="flex-1 bg-[#190276]" />
          <div className="flex-1 bg-[#F2B531]" />
          <div className="flex-1 bg-[#f7040a]" />
        </div>
      </div>
      <div className="bg-gray-100 rounded-lg sm:rounded-xl overflow-hidden mt-1 sm:mt-1.5 relative group/scroll">
        <div className="overflow-hidden px-3 sm:px-4 md:px-8 py-4 sm:py-5">
          <div ref={newRef} className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory" style={{ scrollbarWidth: "none" }}>
            {items.map((p) => (
              <div key={p.id} className="shrink-0 w-[165px] sm:w-[210px] md:w-[235px] lg:w-[240px] snap-start bg-white rounded-xl overflow-hidden">
                <ProductCard product={p} showStock preResolved={bulkResolved[p.id] || null} />
              </div>
            ))}
          </div>
        </div>
        <button type="button" onClick={() => scrollNew(-1)} className="absolute left-1.5 sm:left-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#190276]/80 sm:bg-[#190276] hover:bg-[#130160] text-white flex items-center justify-center transition-all z-10 shadow-lg md:opacity-0 md:group-hover/scroll:opacity-100" aria-label="Précédent"><ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" /></button>
        <button type="button" onClick={() => scrollNew(1)} className="absolute right-1.5 sm:right-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#190276]/80 sm:bg-[#190276] hover:bg-[#130160] text-white flex items-center justify-center transition-all z-10 shadow-lg md:opacity-0 md:group-hover/scroll:opacity-100" aria-label="Suivant"><ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" /></button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   CATÉGORIES — bandeau horizontal scroll
   ═══════════════════════════════════════════════ */
const CategoriesBand = () => (
  <section className="py-4 md:py-6 bg-white" data-testid="catalog-categories">
    <div className={CONTAINER_CLASS}>
      <div className="text-center mb-3 sm:mb-4">
        <h2 className="font-heading text-lg sm:text-xl font-bold text-gray-900 tracking-tight">Notre catalogue</h2>
        <p className="text-[12px] sm:text-[13px] text-gray-400 mt-0.5">Parcourir par famille de produits</p>
      </div>
      <div className="flex w-full justify-center lg:block">
        <nav
          className="flex flex-wrap justify-center gap-x-4 gap-y-3 sm:gap-x-6 sm:gap-y-4 max-w-full lg:grid lg:grid-cols-9 lg:w-full lg:max-w-none lg:gap-x-2 lg:gap-y-2 xl:gap-x-4 lg:justify-items-stretch"
          aria-label="Familles du catalogue"
        >
          {catalogueFamilies.map((f) => (
            <Link
              key={f.id}
              to={f.path}
              className="group flex flex-col items-center gap-2 w-[4.75rem] sm:w-[5.25rem] shrink-0 lg:w-full lg:min-w-0 lg:max-w-none lg:px-2 text-center"
            >
              <div
                className="rounded-2xl bg-gradient-to-br from-[#190276] via-[#F2B531] to-[#f7040a] p-[2px] shadow-sm transition-shadow duration-200 group-hover:shadow-md"
                aria-hidden
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-[13px] border border-gray-100/90 bg-gray-50 transition-all duration-200 group-hover:border-white group-hover:bg-white group-hover:shadow-sm sm:h-16 sm:w-16 sm:rounded-[15px] lg:h-[4.5rem] lg:w-[4.5rem] lg:rounded-2xl xl:h-20 xl:w-20">
                  {f.image && (
                    <img
                      src={f.image}
                      alt=""
                      className="h-8 w-8 object-contain opacity-90 transition-all duration-200 group-hover:scale-105 group-hover:opacity-100 sm:h-9 sm:w-9 lg:h-10 lg:w-10 xl:h-11 xl:w-11"
                    />
                  )}
                </div>
              </div>
              <span className="text-[10px] sm:text-[11px] lg:text-xs font-semibold text-gray-600 group-hover:text-[#190276] transition-colors leading-tight line-clamp-2 min-h-[2.25rem] lg:min-h-[2.5rem] flex items-start justify-center px-0.5">
                {f.label}
              </span>
            </Link>
          ))}
        </nav>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SHOWROOM + ENTREPRISE — même style que ServicesSection
   ═══════════════════════════════════════════════ */
const ShowroomBlock = () => (
  <section className="py-10 sm:py-16 md:py-24 bg-white" data-testid="showroom-block">
    <div className={CONTAINER_CLASS}>
      <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-6 sm:mb-10">
        <span className="text-[#190276] text-[10px] font-bold uppercase tracking-[0.25em]">Showroom & entreprise</span>
        <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mt-2 sm:mt-3 tracking-tight">Showroom 600 m² au Cannet · 5 agences de retrait Côte d'Azur</h2>
      </motion.div>
      <motion.div {...fadeUp} className="grid lg:grid-cols-2 gap-6 sm:gap-10 lg:gap-16 items-center">
        <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
          <img src={EPP.showroom1} alt="Showroom EPP Le Cannet" className="w-full h-[220px] sm:h-[350px] lg:h-[460px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent rounded-2xl" />
          <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg">
            <p className="font-heading text-xl font-black text-[#190276]">+25 ans</p>
            <p className="text-gray-500 text-[11px] font-medium">d'expertise pro</p>
          </div>
        </div>
        <div>
          <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mb-2 sm:mb-3 tracking-tight">Votre partenaire depuis 1999</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
            Entreprise familiale spécialisée en plomberie, chauffage, climatisation, piscines et salle de bains. Comparez, testez et choisissez avec nos experts.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-6 sm:mb-8">
            {[
              { color: "#190276", text: "Showroom 600 m² au Cannet" },
              { color: "#F2B531", text: "5 agences de retrait sur la Côte d'Azur" },
              { color: "#f7040a", text: "Conseil expert & devis gratuit" },
              { color: "#190276", text: "3 000+ références en stock" },
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-2.5 text-gray-700 text-sm"><CheckCircle className="w-4.5 h-4.5 flex-shrink-0" style={{ color: item.color }} /><span className="font-medium">{item.text}</span></li>
            ))}
          </ul>
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:inline-flex gap-3 sm:gap-3">
            <Link to="/showroom" className="rounded-xl overflow-hidden">
              <Button className="btn-blue h-11 px-6 text-sm font-bold w-full justify-center">Découvrir le showroom <ArrowRight className="w-4 h-4 ml-2" /></Button>
              <div className="flex h-[3px]"><div className="flex-1 bg-[#190276]" /><div className="flex-1 bg-[#F2B531]" /><div className="flex-1 bg-[#f7040a]" /></div>
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl border-2 border-gray-200 text-gray-700 text-sm font-bold hover:border-[#190276] hover:text-[#190276] transition-colors">Prendre rendez-vous <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   CHIFFRES — bandeau inline (pas de cartes)
   ═══════════════════════════════════════════════ */
const StatsBand = () => (
  <section className="bg-[#fafbfc] border-y border-gray-100 py-6 sm:py-8 md:py-10">
    <div className={`${CONTAINER_CLASS} grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-2`}>
      {[
        { value: "+25", unit: " ans", label: "d'expertise", accent: "#190276" },
        { value: "5", unit: "", label: "agences de retrait", accent: "#F2B531" },
        { value: "600", unit: " m²", label: "showroom Le Cannet", accent: "#f7040a" },
        { value: "3000+", unit: "", label: "références en stock", accent: "#190276" },
      ].map((item, i) => (
        <motion.div key={i} {...fadeUp} className="text-center">
          <p className="font-heading text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black tracking-tight" style={{ color: item.accent }}>
            {item.value}<span className="text-gray-400 text-xs sm:text-sm md:text-base">{item.unit}</span>
          </p>
          <p className="text-gray-400 text-[9px] sm:text-[10px] md:text-[11px] font-medium mt-1 uppercase tracking-[0.1em] sm:tracking-[0.15em]">{item.label}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   SERVICES — onglets + split (inchangé)
   ═══════════════════════════════════════════════ */
const ServicesSection = () => {
  const [active, setActive] = useState(services[0]);
  return (
    <section id="services" className="py-10 sm:py-16 md:py-24 bg-white" data-testid="services-section">
      <div className={CONTAINER_CLASS}>
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-6 sm:mb-10">
          <span className="text-[#190276] text-[10px] font-bold uppercase tracking-[0.25em]">Nos expertises</span>
          <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mt-2 sm:mt-3 tracking-tight">Plomberie, chauffage, climatisation, piscines, salle de bains</h2>
        </motion.div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6 sm:mb-10">
          {services.map((s, idx) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActive(s)}
              className={`flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-5 py-2.5 sm:py-3 rounded-xl font-bold text-[12px] sm:text-sm transition-all duration-200 min-h-[44px] sm:min-h-0 ${idx === services.length - 1 ? "col-span-2 sm:col-span-1" : ""} ${active.id === s.id ? "text-white shadow-lg" : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"}`}
              style={active.id === s.id ? { backgroundColor: s.color } : {}}
            >
              <s.icon className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" aria-hidden />
              <span className="leading-none whitespace-nowrap">{s.title}</span>
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={active.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="grid lg:grid-cols-2 gap-6 sm:gap-10 lg:gap-16 items-center">
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
              <img src={active.image} alt={active.title} className="w-full h-[220px] sm:h-[350px] lg:h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent rounded-2xl" />
            </div>
            <div>
              <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mb-2 sm:mb-3 tracking-tight">{active.title}</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">{active.description}</p>
              <ul className="grid grid-cols-2 gap-2 sm:gap-3 mb-6 sm:mb-8">
                {active.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-gray-700 text-sm"><CheckCircle className="w-4.5 h-4.5 flex-shrink-0" style={{ color: active.color }} /><span className="font-medium">{f}</span></li>
                ))}
              </ul>
              <div className="grid grid-cols-1 sm:grid-cols-2 sm:inline-flex gap-3">
                <Link to={active.link} className="rounded-xl overflow-hidden">
                  <Button className="btn-blue h-11 px-6 text-sm font-bold w-full justify-center">Voir les produits <ArrowRight className="w-4 h-4 ml-2" /></Button>
                  <div className="flex h-[3px]"><div className="flex-1 bg-[#190276]" /><div className="flex-1 bg-[#F2B531]" /><div className="flex-1 bg-[#f7040a]" /></div>
                </Link>
                <Link to={active.pageLink} className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl border-2 border-gray-200 text-gray-700 text-sm font-bold hover:border-[#190276] hover:text-[#190276] transition-colors">Découvrir cet univers <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};


/* ═══════════════════════════════════════════════
   CLIENTÈLE — colonne titre + cartes (premium)
   ═══════════════════════════════════════════════ */
const CLIENTELE_ITEMS = [
  { icon: HardHat, label: "Professionnels", sub: "Artisans, constructeurs", accent: "#190276", tint: "bg-[#190276]/[0.09]" },
  { icon: Building2, label: "Collectivités", sub: "Copropriétés, syndics", accent: "#F2B531", tint: "bg-[#F2B531]/[0.14]" },
  { icon: Users, label: "Particuliers", sub: "Construction & rénovation", accent: "#f7040a", tint: "bg-[#f7040a]/[0.09]" },
];

const ClienteleBand = () => (
  <section className="relative py-12 md:py-16 border-b border-gray-100 overflow-hidden" data-testid="trust-section">
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#190276]/[0.035] via-white to-[#F2B531]/[0.05]" aria-hidden />
    <div className={`${CONTAINER_CLASS} relative`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-16 xl:gap-20">
        <motion.div {...fadeUp} className="shrink-0 text-center lg:text-left max-w-md mx-auto lg:mx-0">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-[#190276]/10 text-[#190276] mb-5 sm:mb-6 shadow-sm ring-1 ring-[#190276]/10">
            <Shield className="w-5 h-5" strokeWidth={2} aria-hidden />
          </div>
          <span className="block text-[#190276] text-[10px] font-bold uppercase tracking-[0.28em]">Notre clientèle</span>
          <h2 className="font-heading font-black text-gray-900 mt-3 tracking-tight leading-none whitespace-nowrap text-[clamp(1.05rem,3.8vw,2rem)] sm:text-2xl md:text-[2rem]">
            Particuliers{"\u00A0"}&{"\u00A0"}professionnels
          </h2>
          <p className="mt-3 text-[13px] sm:text-sm text-gray-500 leading-relaxed">
            Le même accompagnement et les mêmes références pro, quel que soit votre projet.
          </p>
        </motion.div>

        <ul className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 list-none p-0 m-0">
          {CLIENTELE_ITEMS.map((item, i) => (
            <motion.li
              key={item.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, ease, delay: 0.08 + i * 0.07 }}
              className="group relative rounded-2xl border border-gray-100/90 bg-white/90 backdrop-blur-sm p-5 md:p-6 shadow-[0_2px_20px_-4px_rgba(25,2,118,0.08)] hover:shadow-[0_12px_40px_-12px_rgba(25,2,118,0.18)] hover:border-[#190276]/12 transition-all duration-300"
            >
              <div className="flex flex-col items-start gap-4">
                <div className={`w-12 h-12 rounded-xl ${item.tint} flex items-center justify-center shrink-0 ring-1 ring-black/[0.04] group-hover:scale-[1.03] transition-transform duration-300`}>
                  <item.icon className="w-6 h-6" style={{ color: item.accent }} strokeWidth={2} aria-hidden />
                </div>
                <div className="min-w-0 text-left">
                  <p className="font-heading font-bold text-gray-900 text-[15px] md:text-base tracking-tight">{item.label}</p>
                  <p className="text-gray-500 text-[12px] md:text-[13px] mt-1 leading-snug">{item.sub}</p>
                </div>
              </div>
              <div className="mt-5 flex h-[3px] rounded-full overflow-hidden opacity-70 group-hover:opacity-100 transition-opacity" aria-hidden>
                <div className="flex-1 bg-[#190276]" />
                <div className="flex-1 bg-[#F2B531]" />
                <div className="flex-1 bg-[#f7040a]" />
              </div>
            </motion.li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   AVIS — compact, centré
   ═══════════════════════════════════════════════ */
const TestimonialsSection = () => (
  <section className="py-10 sm:py-14 md:py-20 bg-white" data-testid="testimonials-section">
    <div className={CONTAINER_CLASS}>
      <div className="mx-auto max-w-xl text-center">
      <motion.div {...fadeUp}>
        <div className="flex items-center justify-center gap-1 mb-3 sm:mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-[#F2B531] text-[#F2B531]" />)}</div>
        <h2 className="font-heading text-lg sm:text-xl md:text-2xl font-black text-gray-900 tracking-tight mb-3 sm:mb-4">Ils nous font confiance</h2>
        <p className="text-gray-500 text-[13px] sm:text-sm mb-5 sm:mb-6">Découvrez les avis de nos clients sur Google.</p>
        <a href={googleReviewsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 sm:gap-3 bg-white border-2 border-gray-200 hover:border-[#F2B531] hover:shadow-lg rounded-full px-5 sm:px-8 py-3 sm:py-3.5 text-gray-800 font-bold text-[13px] sm:text-sm transition-all">
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>
          Voir nos avis Google <ArrowRight className="w-4 h-4" />
        </a>
      </motion.div>
      </div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   MARQUES — marquee (inchangé)
   ═══════════════════════════════════════════════ */
const BrandsSection = ({ brands = [] }) => (
  <section id="marques" className="py-10 sm:py-14 md:py-20 bg-[#fafbfc] border-y border-gray-100" data-testid="brands-section">
    <div className={CONTAINER_CLASS}>
      <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-5 sm:mb-8">
        <span className="text-[#F2B531] text-[10px] font-bold uppercase tracking-[0.25em]">Nos partenaires</span>
        <h2 className="font-heading text-lg sm:text-xl md:text-2xl font-black text-gray-900 mt-2 tracking-tight">Marques reconnues</h2>
      </motion.div>
      <Marquee speed={25} gradient={true} gradientWidth={80} pauseOnHover>
        {brands.map((b, i) => (
          <a key={i} href={b.url} target="_blank" rel="noopener noreferrer" className="mx-10 opacity-40 hover:opacity-100 transition-opacity duration-300 grayscale hover:grayscale-0">
            <img src={b.logo} alt={b.name} className="h-10 w-auto object-contain" />
          </a>
        ))}
      </Marquee>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   CAMPAGNES — hors hero : SpotlightBanner (split card) après catégories
   ═══════════════════════════════════════════════ */
function mapRowToSpotlightItem(row) {
  const hook = row.marketing_hook?.trim();
  const sub = row.subtitle?.trim();
  const excerpt = [hook, sub].filter(Boolean).join(" — ") || "";
  const sec = resolveSecondaryMarketingHref(row);
  const hasSec = Boolean(row.cta_secondary_label?.trim() && sec);
  const trackingEntity = row.__source === "event" ? "event" : "campaign";
  const uBadge = storefrontUrgencyPrimary(row);
  const baseTag = sliderTagForPackRow(row);
  return {
    tag: uBadge ? `${baseTag} · ${uBadge}` : baseTag,
    urgencyEndIso: urgencyEndToIso(row.urgency_end_date),
    date: contentEventDateLine(row),
    title: row.title,
    excerpt,
    link: resolveMarketingHref(row),
    image: row.image,
    accent: row.accent_color || "#190276",
    trackingId: row.id,
    trackingEntity,
    secondaryLink: hasSec ? sec : "",
    secondaryLabel: hasSec ? row.cta_secondary_label.trim() : "",
    ctaLabel: row.cta_label || "Découvrir",
    urgencyPill: uBadge || "",
  };
}

const HomeSpotlight = ({ belowRows = [] }) => {
  const items = useMemo(() => (belowRows || []).map(mapRowToSpotlightItem), [belowRows]);
  if (!items.length) return null;
  return <SpotlightBanner items={items} />;
};

/* ═══════════════════════════════════════════════
   CONTACT — split (inchangé)
   ═══════════════════════════════════════════════ */
const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Veuillez remplir les champs obligatoires"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/api/contact`, form, {
        headers: { "Content-Type": "application/json", ...demoHeadersExtra() },
      });
      toast.success("Message envoyé !");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const st = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (st === 403 && detail === "demo_mode_blocked") {
        toast.error("Envoi désactivé en démonstration.");
      } else {
        toast.error("Erreur lors de l'envoi");
      }
    } finally {
      setSending(false);
    }
  };
  return (
    <section id="contact" className="py-10 sm:py-16 md:py-24 bg-white" data-testid="contact-section">
      <div className={CONTAINER_CLASS}>
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <motion.div {...fadeUp}>
            <span className="text-[#F2B531] text-[10px] font-bold uppercase tracking-[0.25em]">Parlons de votre projet</span>
            <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mt-2 sm:mt-3 mb-4 sm:mb-6 tracking-tight">Demandez votre <span className="text-[#F2B531]">devis gratuit</span></h2>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input placeholder="Nom complet *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                <Input type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input type="tel" placeholder="Telephone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger><SelectValue placeholder="Service concerné" /></SelectTrigger>
                  <SelectContent><SelectItem value="plomberie">Plomberie</SelectItem><SelectItem value="chauffage">Chauffage</SelectItem><SelectItem value="climatisation">Climatisation</SelectItem><SelectItem value="piscines">Piscines</SelectItem><SelectItem value="salle-de-bains">Salle de bains</SelectItem><SelectItem value="autre">Autre</SelectItem></SelectContent>
                </Select>
              </div>
              <Textarea placeholder="Votre message *" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
              <Button type="submit" className="btn-yellow w-full sm:w-auto" disabled={sending}>{sending ? "Envoi..." : <><Send className="w-4 h-4 mr-2" /> Envoyer le message</>}</Button>
            </form>
          </motion.div>
          <motion.div {...fadeUp}>
            <div className="relative rounded-xl sm:rounded-2xl p-5 sm:p-8 text-white overflow-hidden" style={{ background: "linear-gradient(135deg, #190276 0%, #130160 50%, #041852 100%)" }}>
              <div className="logo-tricolor-bar absolute left-0 right-0 top-0 w-full" aria-hidden="true" />
              <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Contact principal (siège)</h3>
              <div className="space-y-4 sm:space-y-6">
                {[
                  { icon: MapPin, title: "Adresse siège", text: "33 Chemin des Fades\n06110 Le Cannet" },
                  { icon: Phone, title: "Téléphone", text: "04 93 90 35 50", isPhone: true },
                  { icon: Clock, title: "Horaires", text: "Lun-Ven : 07h30-12h00 / 13h30-17h00\nSam : 08h00-12h00 · Dim : Fermé" },
                ].map((item, i) => {
                  const InfoIcon = item.icon;
                  return (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center shrink-0"><InfoIcon className="w-6 h-6 text-[#F2B531]" /></div>
                      <div>
                        <h4 className="font-bold">{item.title}</h4>
                        {item.isPhone ? <><a href="tel:0493903550" className="text-white/70 hover:text-[#F2B531]">04 93 90 35 50</a><br /><a href="tel:0665948481" className="text-white/70 hover:text-[#F2B531]">06 65 94 84 81</a></> : <p className="text-white/70 whitespace-pre-line">{item.text}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════════════
   STICKY CTA BAR
   ═══════════════════════════════════════════════ */
const StickyCtaBar = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const onScroll = () => setVisible(window.scrollY > 600); window.addEventListener("scroll", onScroll); return () => window.removeEventListener("scroll", onScroll); }, []);
  if (!visible) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex justify-center px-3 sm:px-4">
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="pointer-events-auto max-w-[calc(100vw-1.5rem)]"
      >
        <div className="bg-[#190276]/95 backdrop-blur-md text-white py-2 sm:py-2.5 px-4 sm:px-6 shadow-2xl rounded-full flex items-center gap-3 sm:gap-5 text-[12px] sm:text-sm font-semibold border border-white/10">
          <Link to="/contact" className="inline-flex items-center gap-1.5 sm:gap-2 hover:text-[#F2B531] transition-colors whitespace-nowrap"><Send className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> Devis</Link>
          <div className="w-px h-4 bg-white/20 shrink-0" />
          <a href="tel:0493903550" className="inline-flex items-center gap-1.5 sm:gap-2 hover:text-[#F2B531] transition-colors whitespace-nowrap"><Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> <span className="hidden sm:inline">04 93 90 35 50</span><span className="sm:hidden">Appeler</span></a>
          <div className="w-px h-4 bg-white/20 hidden sm:block shrink-0" />
          <a href="#agences" className="hidden sm:inline-flex items-center gap-2 hover:text-[#F2B531] transition-colors whitespace-nowrap"><MapPin className="w-3.5 h-3.5" /> Agences</a>
        </div>
      </motion.div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   PROMOTIONS RÉELLES — pricing engine
   ═══════════════════════════════════════════════ */
const RealPromosSection = () => {
  const { token } = useUserAuth();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef(null);
  const scrollPromo = (dir) => { const c = sliderRef.current; if (!c) return; const w = c.firstElementChild ? c.firstElementChild.offsetWidth + 16 : 260; c.scrollBy({ left: dir * w, behavior: "smooth" }); };

  useEffect(() => {
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${API}/api/v2/pricing/active-promos?limit=12`, { headers })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => setPromos(d.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="py-4 sm:py-5 md:py-8"><div className={CONTAINER_CLASS}><div className="flex gap-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="shrink-0 w-[220px]"><ProductCardSkeleton /></div>)}</div></div></div>
  );
  if (promos.length === 0) return null;

  return (
    <div className={CONTAINER_CLASS}>
      <div className="bg-[#f7040a] rounded-lg sm:rounded-xl overflow-hidden">
        <div className="px-3 py-2.5 sm:px-5 sm:py-3 md:px-8 md:py-3.5">
          <div className="flex items-center justify-between gap-3 sm:hidden">
            <h2 className="min-w-0 flex-1 font-heading text-[14px] font-black leading-tight tracking-tight text-white">
              Promotions du moment
            </h2>
            <Link
              to="/promos"
              className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-bold text-white/90 underline-offset-2 hover:text-white hover:underline"
            >
              Tout voir <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
            </Link>
          </div>
          <div className="hidden sm:block">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/80">Offres en cours</span>
              <Link
                to="/promos"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-white px-4 py-2 text-[12px] font-bold text-[#f7040a] transition-colors hover:bg-gray-100"
              >
                Voir toutes les promos <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <h2 className="mt-1 font-heading text-base font-black tracking-tight text-white md:text-lg">
              Promotions du moment
            </h2>
          </div>
        </div>
        <div className="flex h-[2px] sm:h-[3px]">
          <div className="flex-1 bg-[#f7040a]" />
          <div className="flex-1 bg-[#F2B531]" />
          <div className="flex-1 bg-[#190276]" />
        </div>
      </div>
      <div className="bg-gray-100 rounded-lg sm:rounded-xl overflow-hidden mt-1 sm:mt-1.5 relative group/scroll">
        <div className="overflow-hidden px-3 sm:px-4 md:px-8 py-4 sm:py-5">
          <div ref={sliderRef} className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory" style={{ scrollbarWidth: "none" }}>
            {promos.map((item) => {
              const product = {
                id: item.product_id,
                name: item.name,
                slug: item.slug,
                brand: item.brand,
                category: item.category,
                image: item.image,
                images: item.image ? [item.image] : [],
                price: item.price_before || item.price,
                badges: [],
              };
              const preResolved = {
                price: item.price,
                price_type: item.price_type || "promo_ttc",
                has_visible_promo: true,
                visible_promo_label: item.promo_label || "Promo",
                price_before_visible_promo: item.price_before,
              };
              return (
                <div key={item.product_id} className="shrink-0 w-[165px] sm:w-[210px] md:w-[235px] lg:w-[240px] snap-start bg-white rounded-xl overflow-hidden">
                  <ProductCard product={product} showStock preResolved={preResolved} />
                </div>
              );
            })}
          </div>
        </div>
        <button type="button" onClick={() => scrollPromo(-1)} className="absolute left-1.5 sm:left-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#f7040a]/80 sm:bg-[#f7040a] hover:bg-[#d50308] text-white flex items-center justify-center transition-all z-10 shadow-lg md:opacity-0 md:group-hover/scroll:opacity-100" aria-label="Précédent"><ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" /></button>
        <button type="button" onClick={() => scrollPromo(1)} className="absolute right-1.5 sm:right-3 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#f7040a]/80 sm:bg-[#f7040a] hover:bg-[#d50308] text-white flex items-center justify-center transition-all z-10 shadow-lg md:opacity-0 md:group-hover/scroll:opacity-100" aria-label="Suivant"><ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" /></button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   CTA FINAL — conversion
   ═══════════════════════════════════════════════ */
const FinalCTA = () => (
  <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-[#190276] via-[#130160] to-[#041852] text-white" data-testid="final-cta">
    <div className={CONTAINER_CLASS}>
      <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
        <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#F2B531]">Prêt à commander ?</span>
        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black mt-3 tracking-tight">
          Trouvez vos matériaux au meilleur prix
        </h2>
        <p className="mt-4 text-sm sm:text-base text-white/70 leading-relaxed">
          3 000+ références en stock, retrait en 2h dans nos 5 agences, prix pro garantis.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <Link
            to="/catalogue"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-[#F2B531] px-7 py-3.5 text-sm font-bold text-gray-900 shadow-lg transition-colors hover:bg-[#e5a82a]"
          >
            <ShoppingCart className="w-4 h-4" /> Voir le catalogue
          </Link>
          <Link
            to="/promos"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-[#f7040a] px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-colors hover:bg-[#d50308]"
          >
            Voir les promotions <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/contact"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-white/25 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10"
          >
            <Send className="w-4 h-4" /> Demander un devis
          </Link>
        </div>
        <div className="mt-6 flex h-[3px] max-w-xs mx-auto rounded-full overflow-hidden">
          <div className="flex-1 bg-[#190276]" />
          <div className="flex-1 bg-[#F2B531]" />
          <div className="flex-1 bg-[#f7040a]" />
        </div>
      </motion.div>
    </div>
  </section>
);

/* ═══════════════════════════════════════════════
   PAGE PRINCIPALE — structure premium conversion
   ═══════════════════════════════════════════════ */
export default function HomePage() {
  const [hpMarketing, setHpMarketing] = useState({ hero: [], below: [], events_preview: [] });
  const [marketingFetchError, setMarketingFetchError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const url = `${API}/api/homepage-marketing`;
    fetch(url)
      .then(async (r) => {
        if (!r.ok) {
          const msg = `HTTP ${r.status} sur homepage-marketing`;
          throw new Error(msg);
        }
        return r.json();
      })
      .then((data) => {
        if (!cancelled) {
          setMarketingFetchError(null);
          setHpMarketing({
            hero: Array.isArray(data?.hero) ? data.hero : [],
            below: Array.isArray(data?.below) ? data.below : [],
            events_preview: Array.isArray(data?.events_preview) ? data.events_preview : [],
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setMarketingFetchError(err?.message || "Erreur réseau");
          setHpMarketing({ hero: [], below: [], events_preview: [] });
        }
      });
    return () => { cancelled = true; };
  }, []);
  const { brands = [] } = useSiteContent();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      {marketingFetchError ? (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-center text-[13px] text-red-800" role="alert">
          Campagnes accueil indisponibles ({marketingFetchError}). Vérifiez <code className="rounded bg-red-100 px-1">REACT_APP_BACKEND_URL</code> et l&apos;API.
        </div>
      ) : null}
      {/* 1. Hero slider — source : GET {API}/api/homepage-marketing → data.hero */}
      <HeroSection apiHeroRows={hpMarketing.hero} />

      {/* Réassurance : uniquement dans Header (fix doublon) */}

      {/* 2. Catégories — bandeau horizontal */}
      <CategoriesBand />

      {/* 3. Produits populaires */}
      <section className="bg-[#fafbfc] py-4 sm:py-5 md:py-8" data-testid="featured-products">
        <FeaturedProducts />
      </section>

      {/* 3b. Spotlight campagnes marketing — après sélection pro */}
      <HomeSpotlight belowRows={hpMarketing.below} />

      {/* 4. Nouveautés */}
      <section className="bg-[#fafbfc] py-4 sm:py-5 md:py-8" data-testid="new-products">
        <NewProducts />
      </section>

      {/* 5. PROMOTIONS RÉELLES — pricing engine */}
      <section className="bg-[#fafbfc] py-4 sm:py-5 md:py-8" data-testid="real-promos">
        <RealPromosSection />
      </section>

      {/* 6. Services — onglets + split */}
      <ServicesSection />

      {/* 7. Showroom — entreprise */}
      <ShowroomBlock />

      {/* 7a. Événements éditoriaux (hors merchandising campagnes) */}
      <HomeEventsPreview rows={hpMarketing.events_preview} />

      {/* 8. Chiffres — bandeau inline */}
      <StatsBand />

      {/* 9. Marques — marquee */}
      <BrandsSection brands={brands} />

      {/* 10. Clientèle */}
      <ClienteleBand />

      {/* 11. Avis — compact */}
      <TestimonialsSection />

      {/* 12. Agences */}
      <AgenciesSection />

      {/* 13. Contact — split */}
      <ContactSection />

      {/* 14. CTA final — conversion */}
      <FinalCTA />

      <StickyCtaBar />
      <div className="h-14" aria-hidden="true" />
    </>
  );
}
